import React from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";

import { useMutation } from "@apollo/client";
import { FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import DatePicker from "components/cck/DatePicker";

import { containerLoadedOrDischargedSchema, containerLoadedOrDischargedSchemaInput, containerLoadedOrDischargedSchemaOutput } from "view/containers/utils/container.schema";

import { BatchUpdateCargosDocument } from "__generated__/view/containers/graphql/mutations.generated";

interface UpdateContainerLoadedDialogProps {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
	containers: { id: number }[],
}

const UpdateContainerLoadedDialog = ({ open, onClose, refetch, containers }: UpdateContainerLoadedDialogProps) => {

	const defaultValues = {
		timestamp: null,
	};

	const form = useForm<containerLoadedOrDischargedSchemaInput, unknown, containerLoadedOrDischargedSchemaOutput>({
		resolver: zodResolver(containerLoadedOrDischargedSchema),
		defaultValues: defaultValues,
	});

	const { control, handleSubmit, reset } = form;

	const [ updateCargos, { loading } ] = useMutation(BatchUpdateCargosDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const save = (data: containerLoadedOrDischargedSchemaOutput) => {
		updateCargos({
			variables: {
				input: {
					ids: containers.map(c => c.id),
					loaded: data.timestamp,
				},
			},
		}).then(() => {
			handleOnClose();
			refetch();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "UPDATE_LOADED" })}
			form='containerLoadedForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="containerLoadedForm">
				<FormGroup label={intl.formatMessage({ id: "CARGO_LOADED" })} overflow="hidden" required>
					<Controller
						name='timestamp'
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker {...field} showTimeInput disablePortal={false} />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default UpdateContainerLoadedDialog;
